/**
 * CZNA-1529 — the authority list's column widths, in pixels of the list as it is measured.
 *
 * Shares of the list (CZNA-1441) could not hold up at both ends. MCL sizes every row to the
 * list's width less 17 px for the vertical scrollbar (MCLRenderer `SCROLLBAR_WIDTH`), so
 * shares summing to 100 % pushed the last column — Stav — 17 px past the grid's right edge.
 * And a share that fits a 1920 px monitor is too narrow at 1280: "Před|měto|vé heslo",
 * "Oph202604958|1" and the status badge broke mid-word. Every column that holds a word that
 * must not break therefore gets that word's width as a floor, measured in the deployed list
 * (Source Sans Pro 14 px, all three translations) plus the cell's 2×10 px padding and 1 px
 * border. As the list grows, the columns get their room in a fixed order (`fullSteps`): the
 * preferred heading — the column the list is searched by — first, the others up to the width
 * that fits them on one line, the heading everything past that. A list narrower than every
 * column's minimum (the detail pane open on a laptop) hides "Autor" and "Aktualizováno" (decided
 * 31. 10. 2026, `narrowSteps`): Stav keeps the widest status word, the heading gets a readable width, the ids
 * wrap if they must; narrower still, the grid scrolls sideways rather than collapse a column.
 *
 * The type column shows only the type's marker until the heading would still keep
 * `HEADING_WITH_TYPE_NAMES` beside the type's full name (decided 31. 10. 2026: on a laptop
 * the marker, the name in its tooltip; on a wide monitor the marker and the name). The names
 * are not a fixed set — the list shows the vocabulary types from the registry too, and each
 * locale names them differently — so their width is not a constant here: the list measures
 * every name it can show in the cell's own font (`typeNameWidth`), and without a measurement
 * the name is not shown at all. A constant once claimed "Formální deskriptor" was the widest;
 * "Město pro staré tisky" was wider and was cut off at 1920 px.
 *
 * A new width is a new `columnWidths` object, and MCL answers that by dropping every row
 * height it measured and re-measuring the rows (MCLRenderer componentDidUpdate → resetCaches),
 * so text that re-wraps after a resize is never drawn over by the next row.
 */
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
/** MCL's own reservation for the vertical scrollbar; a row is never narrower than the list less this. */
var SCROLLBAR = 17;
/** A cell's 2×10 px padding and its 1 px border. */
var CELL_CHROME = 21;
/**
 * The type's 28 px marker under a header that then reads "Typ" / "Type" (33 px bold + the
 * 16 px sort arrow).
 */
var TYPE_MARKER_COLUMN = 72;
/** The compact type label: its 28 px marker and the 7 px gap before the name (AuthorityTypeLabel). */
var TYPE_MARKER = 28;
var TYPE_NAME_GAP = 7;
/**
 * The type column that shows the marker and a name `nameWidth` px wide on one line: rounded up,
 * plus a pixel for a name that lands on a fraction of one.
 */
export var typeNameColumnWidth = function (nameWidth) { return Math.max(TYPE_MARKER_COLUMN, CELL_CHROME + TYPE_MARKER + TYPE_NAME_GAP + Math.ceil(nameWidth) + 1); };
/**
 * A 14-character control number ("Ojo20260507101", 105 px): one token, it cannot wrap. Only a
 * FLOOR since DB-295: ids of the same length are not the same width ("Oaun2026056278" is 109 px
 * and overran this column by 2 px at 1280), so the widest id on the page is measured
 * (`idColumnWidth`) and the column is never narrower than it.
 */
var ID_COLUMN = 128;
/**
 * A status badge wraps between its words (AuthoritiesList), so Stav needs room for the widest
 * single WORD a status has in any translation: "přepracování" in the proposal-status badge,
 * 89 px. Whole phrases run to 272 px ("Amendment proposal: Pending national review"); sizing
 * the column for one of those took the room from the heading.
 */
var STATUS_MIN = 112;
/** …and up to "Návrh změny: Ke schválení v NK" (189 px) on one line where there is room. */
var STATUS_MAX = 212;
/** "Aktualizováno" (96 px bold), the header's one word; the date and the time then take two lines… */
var UPDATED_MIN = 120;
/** …and "31. 10. 2026 12:08" (113 px) on one. */
var UPDATED_MAX = 136;
/** A sigla ("ABA001", 48 px) with room; a person's name wraps between its words. */
var AUTHOR_MIN = 96;
/** "System Administrator" (137 px) on one line. */
var AUTHOR_MAX = 160;
/**
 * The preferred heading is what the list is searched by: the status, the author and the date
 * give up their extra room before it drops below this.
 */
var HEADING_MIN = 180;
/** Where the heading stops taking every pixel and the date, the author and Stav get theirs. */
var HEADING_COMFORTABLE = 300;
/** The heading must keep this much beside the type's name before the name is shown. */
var HEADING_WITH_TYPE_NAMES = 320;
/** What the shares are taken from before the list has been measured (and in jsdom). */
var UNMEASURED_WIDTH = 861;
/**
 * The least a shrinking column gets in a squeezed list: its 2×10 px padding and 1 px border
 * plus a few letters. Below the sum of these the columns stop shrinking and the grid scrolls
 * sideways (MCL sizes a row to its columns), rather than collapse to nothing.
 */
var SQUEEZED_MIN = 44;
/** Half a 14-character control number (53 px) and the cell's padding: an id wraps at most once. */
var ID_WRAPPED = 74;
/** DB-295 — the id column that holds the widest id `idWidth` px wide on one line, never below the floor. */
var idColumnWidth = function (idWidth) { return (idWidth === undefined
    ? ID_COLUMN : Math.max(ID_COLUMN, CELL_CHROME + Math.ceil(idWidth) + 1)); };
/** …and the one that holds its half, so that a squeezed id still breaks at most once. */
var idWrappedWidth = function (idWidth) { return (idWidth === undefined
    ? ID_WRAPPED : Math.max(ID_WRAPPED, CELL_CHROME + Math.ceil(idWidth / 2) + 1)); };
/** The type's 28 px marker and its padding: its header ("Typ", "Type") may then wrap its arrow. */
var TYPE_SQUEEZED = 56;
/** The list's columns in the order a cataloguer scans them (CZNA-1441). */
var columnOrder = function (withMergedInto) { return __spreadArray([
    'type', 'authorityId', 'headingValue', 'author', 'updatedAt', 'validation'
], (withMergedInto ? ['mergedIntoAuthorityId'] : []), true); };
/** Where the heading starts in a list too narrow for the author and the date: still readable. */
var HEADING_NARROW = 130;
/**
 * Who gets the next pixel as the list grows, in order: each step widens one column from its
 * current width up to `to`, and the next starts only once it is there. Within either set of
 * columns every width is therefore a non-decreasing function of the list's width, in whole pixels
 * with nothing to round (shares rounded independently made `typeIconOnly`
 * flicker at 1226–1228 px).
 *
 * With every column shown, each starts at its minimum; then the heading grows to a comfortable
 * width, the date gets its single line, Stav room for a whole status phrase, the author his whole
 * name, and the heading everything after that.
 */
var fullSteps = [
    ['headingValue', HEADING_COMFORTABLE],
    ['updatedAt', UPDATED_MAX],
    ['validation', STATUS_MAX],
    ['author', AUTHOR_MAX],
    ['headingValue', Infinity],
];
/**
 * Decided 31. 10. 2026 — a list too narrow for every column at its minimum (the detail pane
 * open on a laptop: 337 px) drops "Autor" and "Aktualizováno", which the open detail shows
 * anyway. Narrower still it drops "ID autority" too (the detail shows it as well): beside a
 * readable heading it could only wrap into four lines (measured in Chromium, 31. 10. 2026).
 * What is left starts from a few letters each — Stav from the widest status word, an id from
 * half a control number so it breaks at most once — and the heading grows first to a readable
 * width, then the ids so they stop wrapping, then the type's header, then the heading takes the
 * rest. Every column a narrower layout grew is at least as wide in the next one up, so no
 * column ever narrows as the list widens.
 */
var narrowSteps = function (withMergedInto, idShown, idColumn) { return __spreadArray(__spreadArray(__spreadArray([
    ['headingValue', HEADING_NARROW]
], (withMergedInto ? [['mergedIntoAuthorityId', idColumn]] : []), true), (idShown ? [['authorityId', idColumn], ['type', TYPE_MARKER_COLUMN]] : []), true), [
    ['headingValue', Infinity],
], false); };
var total = function (widths) { return Object.values(widths).reduce(function (a, b) { return a + b; }, 0); };
var grow = function (widths, rowWidth, steps) {
    var left = rowWidth - total(widths);
    steps.forEach(function (_a) {
        var column = _a[0], to = _a[1];
        var step = Math.max(0, Math.min(left, to - widths[column]));
        widths[column] += step;
        left -= step;
    });
    return widths;
};
var pixelLayout = function (listWidth, withMergedInto, typeNameWidth, idWidth) {
    var rowWidth = listWidth - SCROLLBAR;
    var idColumn = idColumnWidth(idWidth);
    var idWrapped = idWrappedWidth(idWidth);
    var ids = function (width) { return (__assign({ authorityId: width }, (withMergedInto ? { mergedIntoAuthorityId: width } : {}))); };
    var minimum = __assign(__assign({ type: TYPE_MARKER_COLUMN }, ids(idColumn)), { headingValue: HEADING_MIN, author: AUTHOR_MIN, updatedAt: UPDATED_MIN, validation: STATUS_MIN });
    // Thresholds from the width alone: below each a column is hidden, from it on shown — a resize
    // across one switches once, never back and forth.
    var authorAndDateShown = rowWidth >= total(minimum);
    // The id comes back where it can break at most once beside a readable heading, with
    // "Sloučeno do"'s id already at its full width (as the layout without the id has grown it).
    var idShown = authorAndDateShown || rowWidth >= TYPE_SQUEEZED + STATUS_MIN + HEADING_NARROW
        + idWrapped + (withMergedInto ? idColumn : 0);
    var widths = authorAndDateShown
        ? grow(minimum, rowWidth, fullSteps)
        : grow(__assign(__assign(__assign({ type: TYPE_SQUEEZED }, (idShown ? { authorityId: idWrapped } : {})), (withMergedInto ? { mergedIntoAuthorityId: idWrapped } : {})), { headingValue: SQUEEZED_MIN, 
            // Never below the widest status word: a status must not be cut, however narrow the list.
            validation: STATUS_MIN }), rowWidth, narrowSteps(withMergedInto, idShown, idColumn));
    // The heading beside the marker grows with the list and nothing else does once it is past
    // its comfortable width, so this is a single threshold: the name is shown from the width at
    // which the heading would still keep `HEADING_WITH_TYPE_NAMES` beside the widest name, and
    // the heading pays exactly what the type column gains. Names not measured are not shown: a
    // column sized for a guess cuts off the name that is wider than it.
    var typeNameColumn = typeNameWidth === undefined ? undefined : typeNameColumnWidth(typeNameWidth);
    var typeIconOnly = !authorAndDateShown
        || typeNameColumn === undefined
        || widths.headingValue - (typeNameColumn - TYPE_MARKER_COLUMN) < HEADING_WITH_TYPE_NAMES;
    if (!typeIconOnly && typeNameColumn !== undefined) {
        widths.headingValue -= typeNameColumn - TYPE_MARKER_COLUMN;
        widths.type = typeNameColumn;
    }
    // Ids narrower than a control number wrap inside themselves rather than be cut off.
    var squeezed = Object.keys(ids(0)).some(function (column) { return column in widths && widths[column] < idColumn; });
    var visibleColumns = columnOrder(withMergedInto).filter(function (column) { return column in widths; });
    return { typeIconOnly: typeIconOnly, squeezed: squeezed, visibleColumns: visibleColumns, widths: widths };
};
/**
 * @param listWidth the list's measured width; undefined before it is measured (and in jsdom)
 * @param withMergedInto whether the list shows "Sloučeno do"
 * @param typeNameWidth the widest type name the list can show, as measured in the browser in
 *   the type label's font (`useWidestTextWidth`); undefined where nothing was measured, and
 *   then the type column shows only the marker at any width
 * @param idWidth the widest unbreakable part of an id on the page (DB-295, idUnbreakableParts), measured the same way in the id cell's
 *   font; undefined where nothing was measured, and then the id columns keep their floor
 */
export var authorityListLayout = function (listWidth, withMergedInto, typeNameWidth, idWidth) {
    if (!listWidth) {
        var _a = pixelLayout(UNMEASURED_WIDTH, withMergedInto, undefined), typeIconOnly_1 = _a.typeIconOnly, squeezed_1 = _a.squeezed, visibleColumns_1 = _a.visibleColumns, widths_1 = _a.widths;
        var columnWidths_1 = {};
        Object.entries(widths_1).forEach(function (_a) {
            var column = _a[0], width = _a[1];
            // Rounded down, so the shares never add up to more than the scrollbar leaves.
            columnWidths_1[column] = "".concat(Math.floor((width / UNMEASURED_WIDTH) * 1000) / 10, "%");
        });
        return { typeIconOnly: typeIconOnly_1, squeezed: squeezed_1, visibleColumns: visibleColumns_1, columnWidths: columnWidths_1 };
    }
    var _b = pixelLayout(listWidth, withMergedInto, typeNameWidth, idWidth), typeIconOnly = _b.typeIconOnly, squeezed = _b.squeezed, visibleColumns = _b.visibleColumns, widths = _b.widths;
    var columnWidths = {};
    Object.entries(widths).forEach(function (_a) {
        var column = _a[0], width = _a[1];
        columnWidths[column] = "".concat(width, "px");
    });
    return { typeIconOnly: typeIconOnly, squeezed: squeezed, visibleColumns: visibleColumns, columnWidths: columnWidths };
};
export default authorityListLayout;
